import { useEffect, useState } from "react";
import { Plus, PlayCircle } from "lucide-react";
import { AppLayoutWithHeader } from "@/components/layout/AppLayoutWithHeader";
import { useCrisp } from "@/hooks/useCrisp";

export default function HelpSupport() {
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  
  // Initialiser Crisp (le widget s'affichera automatiquement)
  useCrisp();

  useEffect(() => {
    document.title = "Aide et support | Getproof";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Trouver des réponses, des ressources et contacter notre équipe.");
    } else {
      const m = document.createElement("meta");
      m.name = "description";
      m.content = "Trouver des réponses, des ressources et contacter notre équipe.";
      document.head.appendChild(m);
    }
  }, []);

  const faqSections = [
    {
      title: "Démarrage",
      questions: [
        {
          question: "Comment lancer ma première campagne ?",
          answer: (
            <div className="space-y-3">
              <p><strong>🎯 Processus guidé en 4 étapes :</strong></p>
              <ol className="list-decimal pl-6 space-y-2">
                <li><strong>Informations de base :</strong> nom du candidat, email, poste visé</li>
                <li><strong>Upload des documents :</strong> CV + description de poste (PDF uniquement)</li>
                <li><strong>Validation IA :</strong> l'IA extrait les infos et propose des référents automatiquement</li>
                <li><strong>Personnalisation :</strong> ajustez les critères d'évaluation et lancez</li>
              </ol>
              <div className="bg-blue-50 p-3 rounded-lg mt-4">
                <p className="text-sm"><strong>💡 Conseil :</strong> préparez des documents de qualité pour optimiser l'analyse IA</p>
              </div>
            </div>
          )
        },
        {
          question: "Le candidat peut-il refuser la prise de référence ?",
          answer: (
            <div className="space-y-3">
              <p><strong>✅ Oui, le candidat a le contrôle total :</strong></p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Il reçoit un email de validation avant tout envoi aux référents</li>
                <li>Il peut <strong>accepter</strong> et valider la liste des référents</li>
                <li>Il peut <strong>refuser</strong> et bloquer la campagne</li>
                <li>Il peut <strong>modifier</strong> les contacts des référents si nécessaire</li>
              </ul>
              <div className="bg-green-50 p-3 rounded-lg mt-4">
                <p className="text-sm"><strong>🔒 Sécurité :</strong> conformité RGPD - le candidat reste maître de ses données</p>
              </div>
            </div>
          )
        }
      ]
    },
    {
      title: "IA et analyse",
      questions: [
        {
          question: "Comment l'IA analyse-t-elle mes documents ?",
          answer: (
            <div className="space-y-3">
              <p><strong>📄 Analyse intelligente en 3 étapes :</strong></p>
              <ol className="list-decimal pl-6 space-y-2">
                <li><strong>Extraction des données :</strong> nom, email, expériences, compétences clés</li>
                <li><strong>Scoring de pertinence :</strong> chaque expérience notée selon récence, durée, similitude au poste</li>
                <li><strong>Recommandations ciblées :</strong> référents + critères d'évaluation personnalisés</li>
              </ol>
              <div className="bg-purple-50 p-3 rounded-lg mt-4">
                <p className="text-sm"><strong>⚡ Performance :</strong> 30-60 secondes selon la complexité des documents</p>
              </div>
            </div>
          )
        },
        {
          question: "Comment ajouter des référents ?",
          answer: (
            <div className="space-y-3">
              <p><strong>🤖 L'IA fait le travail pour vous !</strong></p>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Uploadez le CV et la description de poste</li>
                <li>L'IA analyse automatiquement les expériences (score de pertinence sur 10)</li>
                <li>Elle vous propose les référents les plus pertinents avec leur justification</li>
                <li>Sélectionnez ceux qui vous conviennent parmi les suggestions</li>
                <li>Vous pouvez aussi ajouter manuellement d'autres référents</li>
              </ol>
              <div className="bg-blue-50 p-3 rounded-lg mt-4">
                <p className="text-sm"><strong>💡 Conseil :</strong> l'IA privilégie les managers directs et les expériences récentes (&lt;3 ans)</p>
              </div>
            </div>
          )
        },
        {
          question: "Que signifient les priorités des référents ?",
          answer: (
            <div className="space-y-3">
              <p><strong>📊 Système de scoring intelligent :</strong></p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-green-50 p-3 rounded">
                  <p className="font-semibold text-green-700">🟢 Haute (8-10)</p>
                  <p className="text-sm">Manager direct, expérience récente, longue durée</p>
                </div>
                <div className="bg-yellow-50 p-3 rounded">
                  <p className="font-semibold text-yellow-700">🟡 Moyenne (5-7)</p>
                  <p className="text-sm">Collègue, expérience pertinente, durée correcte</p>
                </div>
                <div className="bg-red-50 p-3 rounded">
                  <p className="font-semibold text-red-700">🔴 Faible (1-4)</p>
                  <p className="text-sm">Contact distant, expérience ancienne ou courte</p>
                </div>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg mt-4">
                <p className="text-sm"><strong>🎯 Recommandation :</strong> privilégiez les référents à priorité haute pour des résultats optimaux</p>
              </div>
            </div>
          )
        },
        {
          question: "Que faire si l'IA se trompe dans son analyse ?",
          answer: (
            <div className="space-y-3">
              <p><strong>🔧 Processus de validation et correction :</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Étape de validation :</strong> vérifiez toutes les informations extraites avant validation</li>
                <li><strong>Modification manuelle :</strong> corrigez le nom, email, poste directement dans les champs</li>
                <li><strong>Référents personnalisés :</strong> ajoutez/supprimez des référents selon votre connaissance</li>
                <li><strong>Critères ajustables :</strong> modifiez les critères d'évaluation générés automatiquement</li>
              </ul>
              <div className="bg-red-50 p-3 rounded-lg mt-4">
                <p className="text-sm"><strong>⚠️ Important :</strong> toujours valider les informations critiques (email candidat, poste visé)</p>
              </div>
            </div>
          )
        }
      ]
    },
    {
      title: "Gestion des campagnes",
      questions: [
        {
          question: "Comment personnaliser les critères d'évaluation ?",
          answer: (
            <div className="space-y-3">
              <p><strong>🎨 Personnalisation complète des critères :</strong></p>
              <ol className="list-decimal pl-6 space-y-2">
                <li><strong>Critères générés par l'IA :</strong> basés sur le poste et les expériences du candidat</li>
                <li><strong>Modification en temps réel :</strong> éditez le titre, la description et le poids de chaque critère</li>
                <li><strong>Ajout de nouveaux critères :</strong> créez des critères spécifiques à votre entreprise</li>
                <li><strong>Réorganisation :</strong> changez l'ordre d'importance par glisser-déposer</li>
                <li><strong>Suppression sélective :</strong> retirez les critères non pertinents</li>
              </ol>
              <div className="bg-indigo-50 p-3 rounded-lg mt-4">
                <p className="text-sm"><strong>🎯 Exemple :</strong> pour un poste commercial → l'IA ajoute automatiquement "Atteinte des objectifs" et "Relation client"</p>
              </div>
            </div>
          )
        },
        {
          question: "Mes emails n'arrivent pas aux référents, que faire ?",
          answer: (
            <div className="space-y-3">
              <p><strong>🔍 Diagnostic étape par étape :</strong></p>
              <ol className="list-decimal pl-6 space-y-2">
                <li><strong>Vérification immédiate :</strong> format email correct, pas de fautes de frappe</li>
                <li><strong>Dossiers spam :</strong> demandez aux référents de vérifier leurs courriers indésirables</li>
                <li><strong>Filtres entreprise :</strong> certaines entreprises bloquent les emails externes</li>
                <li><strong>Renvoi automatique :</strong> utilisez le bouton "Renvoyer" dans le dashboard</li>
                <li><strong>Support dédié :</strong> contactez-nous avec l'ID de campagne pour investigation</li>
              </ol>
              <div className="bg-yellow-50 p-3 rounded-lg mt-4">
                <p className="text-sm"><strong>📊 Statistiques :</strong> consultez le taux de livraison en temps réel dans votre dashboard</p>
              </div>
            </div>
          )
        }
      ]
    },
    {
      title: "Rapports & fonctionnalités",
      questions: [
        {
          question: "Où consulter et partager les rapports ?",
          answer: (
            <div className="space-y-3">
              <p><strong>📈 Centre de rapports complet :</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Onglet "Rapports" :</strong> vue d'ensemble de toutes vos campagnes terminées</li>
                <li><strong>Filtres avancés :</strong> par date, candidat, statut de campagne</li>
                <li><strong>Exports multiples :</strong> PDF professionnel, Excel pour analyse, lien de partage sécurisé</li>
                <li><strong>Analyse IA :</strong> synthèse automatique des points forts et axes d'amélioration</li>
                <li><strong>Scores détaillés :</strong> graphiques radar, notes par critère, recommandations finales</li>
              </ul>
              <div className="bg-green-50 p-3 rounded-lg mt-4">
                <p className="text-sm"><strong>🔗 Partage sécurisé :</strong> générez des liens temporaires pour partager avec votre équipe ou le candidat</p>
              </div>
            </div>
          )
        },
        {
          question: "Mes données sont-elles protégées et conformes RGPD ?",
          answer: (
            <div className="space-y-3">
              <p><strong>🔒 Sécurité et conformité maximales :</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Chiffrement bout en bout :</strong> toutes les données sont chiffrées en transit et au repos</li>
                <li><strong>Conformité RGPD :</strong> droit à l'oubli, portabilité, consentement explicite</li>
                <li><strong>Hébergement sécurisé :</strong> serveurs européens certifiés ISO 27001</li>
                <li><strong>Accès contrôlé :</strong> authentification forte, logs d'activité, permissions granulaires</li>
                <li><strong>Rétention limitée :</strong> suppression automatique après 3 ans sauf demande contraire</li>
              </ul>
              <div className="bg-blue-50 p-3 rounded-lg mt-4">
                <p className="text-sm"><strong>📜 Documentation :</strong> consultez notre charte de véracité et nos conditions d'utilisation dans l'onglet Tutoriels</p>
              </div>
            </div>
          )
        }
      ]
    }
  ];

  return (
    <AppLayoutWithHeader 
      title="Aide et support"
      subtitle="Trouver des réponses, des ressources et contacter notre équipe."
    >
      {/* Main Content */}
      <div className="flex gap-12">
              {/* FAQ Section */}
              <div className="flex-1 flex flex-col gap-6">
                <h2 className="text-gray-800 text-2xl font-medium">Questions fréquemment posées</h2>
                {faqSections.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="flex flex-col gap-3">
                    <h3 className="text-gray-800 text-xl font-medium">{section.title}</h3>
                    <div className="flex flex-col gap-3">
                      {section.questions.map((item, questionIndex) => {
                        const faqId = `${sectionIndex}-${questionIndex}`;
                        const isExpanded = expandedFaq === faqId;
                        return (
                          <div key={questionIndex} className="bg-white rounded-lg border border-neutral-400/20">
                            <button
                              onClick={() => setExpandedFaq(isExpanded ? null : faqId)}
                              className="w-full p-4 flex justify-between items-center hover:border-teal-400 transition-colors text-left"
                            >
                              <span className="text-gray-800 text-base font-medium">{item.question}</span>
                              <Plus className={`w-6 h-6 text-gray-400 transition-transform ${isExpanded ? 'rotate-45' : ''}`} />
                            </button>
                            {isExpanded && (
                              <div className="px-4 pb-4 text-sm text-gray-600 border-t border-gray-100 pt-3">
                                {item.answer}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Academy Section */}
              <div className="flex-1 flex flex-col items-center justify-start pt-8">
                {/* Illustration depuis Supabase Storage */}
                <div className="relative mb-12 flex justify-center">
                  <img 
                    src="https://pvmemyboxgefsaordzqj.supabase.co/storage/v1/object/public/App%20image/image_2025-09-15_232037792%201.svg"
                    alt="Illustration d'aide et support avec personnages"
                    className="w-80 h-72 object-contain"
                    loading="lazy"
                  />
                </div>

                {/* Academy Card */}
                <div className="flex flex-col items-center gap-7">
                  <div className="flex flex-col items-center gap-3">
                    <h2 className="text-gray-800 text-3xl font-medium text-center">Académie GetProof</h2>
                    <p className="w-full max-w-md text-center text-neutral-400 text-base font-medium leading-normal">
                      Accédez à notre vaste bibliothèque vidéo pour maîtriser la 
                      plateforme avec des tutoriels, des bonnes pratiques et des 
                      conseils d'experts.
                    </p>
                  </div>
                  <button className="px-3 py-3.5 rounded-lg border border-teal-400 flex items-center gap-2 hover:bg-teal-50 transition-colors">
                    <PlayCircle className="w-5 h-5 text-teal-400" />
                    <span className="text-teal-400 text-sm font-medium">Accéder à la galerie</span>
                  </button>
                </div>
              </div>
      </div>


    </AppLayoutWithHeader>
  );
}